import { Container, Paper, Stack, Title, Text, Group, Button, Badge, Box, Divider, ThemeIcon } from '@mantine/core';
import { IconPackage, IconCheck, IconHome, IconEye } from '@tabler/icons-react';
import { NavigateFunction } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';

interface SuccessScreenProps {
    packageCount: number;
    navigate: NavigateFunction;
    responseData: any;
    totalWeight: number;
    customValue: number | undefined;
}

const SuccessScreen = ({ packageCount, navigate, responseData, totalWeight, customValue }: SuccessScreenProps) => {
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Extract combined package ID from the first package if available
    const combinedPackageId = responseData?.package?.[0]?.combinedPackageId;

    // Calculate total number of items
    const packageCount2 = responseData?.package?.length || packageCount;

    // Responsive sizing
    const containerSize = isMobile ? 'sm' : 'md';
    const paperPadding = isMobile ? 'md' : 'xl';
    const buttonSize = isMobile ? 'sm' : 'md';
    const iconSize = isMobile ? 60 : 80;
    const checkIconSize = isMobile ? 30 : 40;

    return (
        <Container size={containerSize} py={isMobile ? "md" : "xl"}>
            <Paper p={paperPadding} radius="md" withBorder>
                <Stack align="center" gap={isMobile ? "md" : "xl"}>
                    {/* Success Icon */}
                    <ThemeIcon
                        size={iconSize}
                        radius="50%"
                        variant="light"
                        color="green"
                        style={{
                            backgroundColor: 'var(--mantine-color-green-1)',
                            border: isMobile ? '3px solid var(--mantine-color-green-2)' : '4px solid var(--mantine-color-green-2)'
                        }}
                    >
                        <IconCheck size={checkIconSize} />
                    </ThemeIcon>

                    {/* Success Title */}
                    <Title
                        order={isMobile ? 3 : 2}
                        ta="center"
                        style={{
                            color: 'var(--mantine-color-green-7)',
                            lineHeight: 1.3
                        }}
                    >
                        Consolidation Request Successful!
                    </Title>

                    {/* Success Message */}
                    <Text
                        size={isMobile ? "md" : "lg"}
                        ta="center"
                        c="dimmed"
                        style={{ maxWidth: isMobile ? '100%' : '400px' }}
                    >
                        Your {packageCount2} packages have been submitted for consolidation.
                    </Text>

                    {/* Details Card */}
                    <Paper
                        withBorder
                        p={isMobile ? "sm" : "md"}
                        radius="md"
                        w="100%"
                        style={{
                            backgroundColor: 'var(--mantine-color-gray-0)',
                            border: '1px solid var(--mantine-color-gray-3)'
                        }}
                    >
                        <Stack gap={isMobile ? "xs" : "sm"}>
                            {/* Request ID */}
                            <Group justify="space-between" wrap="nowrap">
                                <Text fw={500} size={isMobile ? "sm" : "md"}>Request ID:</Text>
                                <Badge
                                    color="blue"
                                    size={isMobile ? "sm" : "md"}
                                    variant="light"
                                >
                                    #{combinedPackageId || 'Processing'}
                                </Badge>
                            </Group>

                            <Divider />

                            {/* Package Count */}
                            <Group justify="space-between" wrap="nowrap">
                                <Text fw={500} size={isMobile ? "sm" : "md"}>Total Packages:</Text>
                                <Text size={isMobile ? "sm" : "md"} fw={500}>
                                    {packageCount2}
                                </Text>
                            </Group>

                            {/* Total Weight */}
                            <Group justify="space-between" wrap="nowrap">
                                <Text fw={500} size={isMobile ? "sm" : "md"}>Total Weight:</Text>
                                <Text size={isMobile ? "sm" : "md"} fw={500}>
                                    {totalWeight.toFixed(2)} kg
                                </Text>
                            </Group>

                            {/* Declared Value */}
                            <Group justify="space-between" wrap="nowrap">
                                <Text fw={500} size={isMobile ? "sm" : "md"}>Declared Value:</Text>
                                <Text size={isMobile ? "sm" : "md"} fw={500} c="green">
                                    ${customValue?.toFixed(2)}
                                </Text>
                            </Group>
                        </Stack>
                    </Paper>

                    {/* Processing Info */}
                    <Box
                        p={isMobile ? "sm" : "md"}
                        style={{
                            backgroundColor: 'var(--mantine-color-blue-0)',
                            borderRadius: 'var(--mantine-radius-md)',
                            border: '1px solid var(--mantine-color-blue-2)',
                            textAlign: 'center'
                        }}
                    >
                        <Group justify="center" gap="xs" mb="xs">
                            <IconPackage
                                size={isMobile ? 14 : 16}
                                style={{ color: 'var(--mantine-color-blue-6)' }}
                            />
                            <Text
                                size={isMobile ? "xs" : "sm"}
                                c="blue.7"
                                fw={500}
                            >
                                Processing Timeline
                            </Text>
                        </Group>
                        <Text
                            size={isMobile ? "xs" : "sm"}
                            c="dimmed"
                            style={{ lineHeight: 1.4 }}
                        >
                            Your consolidation request will appear in your dashboard within 4-5 hours.
                            We'll notify you once processing is complete.
                        </Text>
                    </Box>

                    {/* Action Buttons */}
                    <Stack
                        gap={isMobile ? "xs" : "sm"}
                        w={isMobile ? "100%" : "auto"}
                        mt={isMobile ? "md" : "xl"}
                    >
                        <Group
                            gap={isMobile ? "xs" : "sm"}
                            style={isMobile ? { flexDirection: 'column', width: '100%' } : {}}
                        >
                            <Button
                                onClick={() => navigate('/dashboard')}
                                variant="outline"
                                size={buttonSize}
                                leftSection={<IconHome size={isMobile ? 14 : 16} />}
                                style={isMobile ? { width: '100%' } : {}}
                            >
                                Go to Dashboard
                            </Button>
                            <Button
                                onClick={() => navigate('/packages')}
                                size={buttonSize}
                                leftSection={<IconEye size={isMobile ? 14 : 16} />}
                                style={isMobile ? { width: '100%' } : {}}
                            >
                                View All Packages
                            </Button>
                        </Group>
                    </Stack>
                </Stack>
            </Paper>
        </Container>
    );
};

export default SuccessScreen;
